<template>
    <div :class="$style.container">
        <div :class="withBadges && $style.titleWithBadges">
            <template v-if="props.link">
                <Typography
                    is="h2"
                    v-if="props.title"
                    type="label"
                    size="l"
                    accent
                    :responsive="false"
                    :class="$style.title"
                    data-interface-target="navigation-link"
                >
                    <BaseLink
                        :to="props.link"
                        tabindex="-1"
                    >
                        {{ props.title }}
                    </BaseLink>
                    <BaseLink :to="props.link">
                        <SeoOnly>
                            {{ props.title }}
                        </SeoOnly>
                        <IconButton
                            icon="chevron-right"
                            color="carbon-400"
                            size="s"
                            interfaceTarget="navigation-link"
                            :class="$style.title_button_override"
                        />
                    </BaseLink>
                </Typography>
            </template>
            <Typography
                v-else-if="props.title"
                type="label"
                size="l"
                :responsive="false"
                :class="$style.title"
            >
                {{ props.title }}
            </Typography>
        </div>
        <div :class="[$style.scroller_wrap, props.withBadges && $style.scroller_wrap_with_badges]">
            <div
                ref="scrollerRef"
                :class="[$style.scroller, props.dummy && $style.scroller_empty]"
                :style="fixedBaseSizeStyle"
                @scroll="onScroll"
            >
                <template
                    v-for="(item, index) in props.items"
                    :key="index"
                >
                    <slot
                        :item="item"
                        :index="index"
                        :className="$style.card_override"
                    />
                </template>
                <IconButton
                    v-if="props.lastItemLink"
                    icon="chevron-right"
                    color="carbon-400"
                    interfaceTarget="last-carousel-item"
                    size="l"
                    :to="props.lastItemLink"
                    :class="[$style.card_override, $style.card_next_override]"
                />
            </div>
            <button
                tabindex="-1"
                :class="[
                    leftArrowActive && $style.arrow_active,
                    props.roundScrollButtons ? $style.round_scroll_arrow_left : $style.arrow_left,
                ]"
                @click="onArrowLeftClick"
            >
                <Icon name="chevron-left" />
            </button>
            <button
                tabindex="-1"
                :class="[
                    rightArrowActive && $style.arrow_active,
                    props.roundScrollButtons ? $style.round_scroll_arrow_right : $style.arrow_right,
                ]"
                @click="onArrowRightClick"
            >
                <Icon name="chevron-right" />
            </button>
        </div>
    </div>
</template>
<script lang="ts" setup generic="T">
import { computed, ref, useCssModule } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import IconButton from '@/components/Buttons/IconButton.vue'
import Icon from '@/components/Icon/Icon.vue'
import Typography from '@/components/Typography.vue'
import { useLoggerStore } from '@/store/logger'
import { AvailableEvent } from '@/types/logger.types'

import SeoOnly from './SeoOnly.vue'

interface CarouselProps {
    items: T[]
    dummy?: boolean
    title?: string
    link?: RouteLocationRaw
    rows?: number
    lastItemLink?: RouteLocationRaw
    withBadges?: boolean
    fixedBaseSize?: string
    roundScrollButtons?: boolean
}

const props = withDefaults(defineProps<CarouselProps>(), {
    rows: 1,
})

const fixedBaseSizeStyle = computed(() => {
    if (props.fixedBaseSize) {
        return { '--base-size': props.fixedBaseSize }
    }
    return {}
})

const carouselRows = computed(() => props.rows)

const classes = useCssModule()
const loggerStore = useLoggerStore()

const scrollerRef = ref<HTMLElement | null>(null)
const leftArrowActive = ref(false)
const rightArrowActive = ref(true)
const scrollEventSent = ref(false)

const handleInterfaceScroll = (event: Event) => {
    const target = event.target as HTMLElement
    const gaScrollCardCarouselContainer = target.closest('[data-interface-section]') as HTMLElement | null
    if (!gaScrollCardCarouselContainer) {
        return
    }
    const eventProps = {
        interface_target: gaScrollCardCarouselContainer.dataset.interfaceSection as string,
        eventName: 'interface_scroll' as AvailableEvent,
    }
    loggerStore.logInterfaceEvents(eventProps)
    scrollEventSent.value = true
}

function onScroll(e: Event) {
    const scroller = e.currentTarget
    if (!(scroller instanceof HTMLElement)) {
        return
    }
    const notEmpty = !props.dummy
    leftArrowActive.value = notEmpty && scroller.scrollLeft > 0
    rightArrowActive.value = notEmpty && scroller.scrollLeft + scroller.clientWidth + 1 < scroller.scrollWidth
    if (!scrollEventSent.value) {
        handleInterfaceScroll(e)
    }
}

function getScrollStepSize() {
    // get the width of the second card (first card sometimes could be big one)
    const card = scrollerRef.value?.querySelector(`.${classes.card_override}:nth-child(2)`)
    if (!card) {
        return 0
    }

    const computedStyle = getComputedStyle(card)
    const cardWidth = parseInt(computedStyle.getPropertyValue('width'), 10)
    let cardsCount = parseInt(computedStyle.getPropertyValue('--count'), 10)

    const gap = parseInt(computedStyle.getPropertyValue('--gap'), 10)

    return cardsCount * cardWidth + (cardsCount - 1) * gap
}

function onArrowLeftClick() {
    const scroller = scrollerRef.value
    if (scroller) {
        const scrollSize = getScrollStepSize()
        scroller.scroll({
            left: Math.max(0, scroller.scrollLeft - scrollSize),
            behavior: 'smooth',
        })
    }
}
function onArrowRightClick() {
    const scroller = scrollerRef.value
    if (scroller) {
        const scrollSize = getScrollStepSize()
        scroller.scroll({
            left: Math.min(scroller.scrollWidth - scroller.clientWidth, scroller.scrollLeft + scrollSize),
            behavior: 'smooth',
        })
    }
}
</script>
<style module>
.container {
    --scroller-arrow-size: 40px;
}

.title {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
}

.titleWithBadges {
    margin-bottom: 5px;
}

.title_button_override {
    margin-left: 8px;
    opacity: 0;
    transition: opacity 0.1s ease;
}

.container:hover .title_button_override,
.container:focus-within .title_button_override {
    opacity: 1;
}

@media (hover: none) {
    .title_button_override {
        opacity: 1;
    }
}

.scroller_wrap {
    position: relative;
    margin-left: calc((var(--global-padding-horizontal) + var(--pg-safe-area-right)) * -1);
    margin-right: calc((var(--global-padding-horizontal) + var(--pg-safe-area-left) * -1));
}

.scroller_wrap_with_badges {
    margin: -3px 0 0 -3px;
}

.arrow_left,
.arrow_right {
    position: absolute;
    top: 0;
    bottom: 0;
    width: var(--scroller-arrow-size);
    padding: 0 8px;
    box-sizing: border-box;
    display: none;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    --transition-duration: 0.15s;
    --transition-fn: ease-in-out;
    transition:
        var(--transition-duration) var(--transition-fn) opacity,
        var(--transition-duration) var(--transition-fn) transform;
}

.arrow_left {
    left: 0;
    transform: translateX(-10px);
    background: linear-gradient(to right, #0b0b0c, transparent);
}

.arrow_right {
    right: 0;
    transform: translateX(10px);
    background: linear-gradient(to left, #0b0b0c, transparent);
}

.round_scroll_arrow_left,
.round_scroll_arrow_right {
    background: #000000aa;
    position: absolute;
    top: 50%;

    width: var(--scroller-arrow-size);
    height: var(--scroller-arrow-size);
    border-radius: var(--scroller-arrow-size);
    padding: 0 8px;
    box-sizing: border-box;
    display: none;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    --transition-duration: 0.15s;
    --transition-fn: ease-in-out;
    transition:
        var(--transition-duration) var(--transition-fn) opacity,
        var(--transition-duration) var(--transition-fn) transform;
}

.round_scroll_arrow_left {
    left: 0;
    transform: translateX(-10px);
}

.round_scroll_arrow_right {
    right: 0;
    transform: translateX(10px);
}

@media (--tablet-plus) {
    .arrow_active {
        opacity: 1;
        display: flex;
        transform: translateX(0px);
        transition:
            var(--transition-duration) var(--transition-fn) opacity,
            var(--transition-duration) var(--transition-fn) transform;
    }
}

.scroller {
    overflow: auto;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    scroll-padding: var(--scroller-arrow-size);
    scrollbar-width: none;

    padding-left: var(--global-padding-horizontal);

    /* base-size is calculated so that there are --count regular cards + half of one more on the screen */
    /* but not less than 160px and not more than 220px */
    --base-size: clamp(160px, calc((100% - var(--gap) * var(--count)) / (var(--count) + 0.5)), 220px);
    /* final card size */
    --size: var(--base-size);
    --rows: v-bind(carouselRows);
    --gap: 8px;

    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: var(--size);
    grid-template-rows: repeat(var(--rows), 1fr);
    gap: var(--gap) 4px;
    --cards-gap: 8; /* unitless, consumed by GameCard component */
}

.scroller::-webkit-scrollbar {
    display: none;
}

.scroller_empty {
    overflow: hidden;
}

/* Custom widths for specific number of visible cards, considers layout columns jumps */

.scroller {
    --count: 2;
}
@media (width >= 590px) {
    .scroller {
        --count: 3;
    }
}
@media (width >= 819px) {
    .scroller {
        --count: 4;
    }
}
@media (width >= 1381px) {
    .scroller {
        --count: 5;
    }
}
@media (width >= 2097px) {
    .scroller {
        --count: 6;
    }
}
@media (width >= 2440px) {
    .scroller {
        --count: 7;
    }
}

.card_override {
    scroll-snap-align: start;
    scroll-snap-stop: normal;
}

.scroller_wrap_with_badges .card_override {
    margin: 3px 0 0 3px;
}

/* make heavier selector to override button's styles, TODO refactor */
.scroller .card_next_override {
    height: auto;
    --border-radius: 12px;
    grid-row: span var(--rows);
}
</style>
